/**
 * AdmissionsDocuments — admin config for required documents.
 *
 * Per-org checklist. Each doc has: title, description, kind (signature /
 * upload / both), category (agreement / consent / health / other), body
 * (markdown), required flag, order index, active flag.
 *
 * Drag-to-reorder is out of scope for v1; use the order field.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2, Save } from "lucide-react";
import { api } from "../../lib/api";
import { useI18n } from "../../lib/i18n";
import { AdminHeader } from "./_shared";

export default function AdmissionsDocuments() {
  const { t } = useI18n();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/admissions/required-docs");
      setDocs(r.data || []);
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () =>
    setEditing({
      title: "",
      description: "",
      kind: "e_signature",
      category: "agreement",
      body_markdown: "",
      is_required: true,
      order_index: docs.length + 1,
    });

  const save = async (form) => {
    try {
      if (form.id) {
        const r = await api.patch(
          `/admin/admissions/required-docs/${form.id}`,
          form,
        );
        setDocs((prev) => prev.map((d) => (d.id === form.id ? r.data : d)));
      } else {
        const r = await api.post("/admin/admissions/required-docs", form);
        setDocs((prev) => [...prev, r.data]);
      }
      toast.success("Saved.");
      setEditing(null);
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    }
  };

  const remove = async (docId) => {
    if (!window.confirm("Remove this document?")) return;
    try {
      await api.delete(`/admin/admissions/required-docs/${docId}`);
      setDocs((prev) => prev.filter((d) => d.id !== docId));
      toast.success("Removed.");
    } catch (e) {
      toast.error(t("admissions.common.error_generic"));
    }
  };

  return (
    <div data-testid="admin-admissions-documents" className="space-y-6">
      <Link
        to="/admin/admissions"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#1F3A2A]"
      >
        <ArrowLeft size={14} />
        {t("admissions.lead_detail.back_to_pipeline")}
      </Link>

      <AdminHeader
        eyebrow={t("admissions.nav_admin")}
        title={t("admissions.documents_admin.title")}
        action={
          <button
            type="button"
            data-testid="admin-admissions-add-doc"
            onClick={openNew}
            className="btn-primary !py-2 !px-4 !text-sm inline-flex items-center gap-1.5"
          >
            <Plus size={14} />
            {t("admissions.documents_admin.add_doc")}
          </button>
        }
      />
      <p className="text-sm text-[#6B7280] -mt-4 max-w-2xl">
        {t("admissions.documents_admin.subtitle")}
      </p>

      {loading ? (
        <p className="text-sm text-[#6B7280] italic">{t("admissions.common.loading")}</p>
      ) : docs.length === 0 ? (
        <p
          data-testid="admin-admissions-docs-empty"
          className="rounded-2xl border border-dashed border-[#EEF0F4] bg-white p-10 text-center text-sm text-[#6B7280]"
        >
          {t("admissions.documents_admin.empty")}
        </p>
      ) : (
        <ul className="space-y-3" data-testid="admin-admissions-docs-list">
          {docs.map((d) => (
            <li
              key={d.id}
              data-testid={`admin-admissions-doc-${d.id}`}
              className="rounded-2xl border border-[#EEF0F4] bg-white p-5"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="font-display text-lg text-[#1A1B2E]">{d.title}</h3>
                    {d.is_required && (
                      <span className="text-[10px] font-semibold tracking-wider uppercase text-[#1F3A2A] bg-[#E6EFE8] rounded-full px-2 py-0.5">
                        Required
                      </span>
                    )}
                    <span className="text-[10px] text-[#6B7280] tracking-wider uppercase">
                      {d.kind.replace("_", " ")} · {d.category}
                    </span>
                  </div>
                  <p className="mt-1.5 text-sm text-[#6B7280]">{d.description}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    data-testid={`admin-admissions-doc-edit-${d.id}`}
                    onClick={() => setEditing({ ...d })}
                    className="text-xs font-semibold text-[#1F3A2A] underline underline-offset-4"
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    data-testid={`admin-admissions-doc-delete-${d.id}`}
                    onClick={() => remove(d.id)}
                    className="text-xs text-[#B91C1C] hover:text-[#7F1D1D]"
                    aria-label="Delete"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <DocEditor
          initial={editing}
          onCancel={() => setEditing(null)}
          onSave={save}
          t={t}
        />
      )}
    </div>
  );
}

function DocEditor({ initial, onCancel, onSave, t }) {
  const [form, setForm] = useState(initial);
  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <div
      data-testid="admin-admissions-doc-editor"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F2018]/55 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-2xl rounded-3xl bg-white border border-[#EEF0F4] p-7 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="eyebrow">
          {initial.id
            ? t("admissions.documents_admin.edit_doc")
            : t("admissions.documents_admin.add_doc")}
        </div>
        <form onSubmit={submit} className="mt-5 space-y-4">
          <Field label={t("admissions.documents_admin.field_title")}>
            <input
              data-testid="admin-admissions-doc-title"
              type="text"
              required
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              className="input-soft w-full"
            />
          </Field>
          <Field label={t("admissions.documents_admin.field_description")}>
            <input
              data-testid="admin-admissions-doc-description"
              type="text"
              required
              value={form.description}
              onChange={(e) => update("description", e.target.value)}
              className="input-soft w-full"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("admissions.documents_admin.field_kind")}>
              <select
                data-testid="admin-admissions-doc-kind"
                value={form.kind}
                onChange={(e) => update("kind", e.target.value)}
                className="input-soft w-full"
              >
                <option value="e_signature">
                  {t("admissions.documents_admin.field_kind_signature")}
                </option>
                <option value="upload">{t("admissions.documents_admin.field_kind_upload")}</option>
                <option value="both">{t("admissions.documents_admin.field_kind_both")}</option>
              </select>
            </Field>
            <Field label={t("admissions.documents_admin.field_category")}>
              <select
                data-testid="admin-admissions-doc-category"
                value={form.category}
                onChange={(e) => update("category", e.target.value)}
                className="input-soft w-full"
              >
                <option value="agreement">Agreement</option>
                <option value="consent">Consent</option>
                <option value="health">Health</option>
                <option value="other">Other</option>
              </select>
            </Field>
          </div>
          {(form.kind === "e_signature" || form.kind === "both") && (
            <Field label={t("admissions.documents_admin.field_body")}>
              <textarea
                data-testid="admin-admissions-doc-body"
                rows={10}
                value={form.body_markdown || ""}
                onChange={(e) => update("body_markdown", e.target.value)}
                className="input-soft w-full font-mono text-sm"
                placeholder="# Agreement title&#10;&#10;## Quiet hours&#10;..."
              />
            </Field>
          )}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-[#1A1B2E]">
              <input
                type="checkbox"
                data-testid="admin-admissions-doc-required"
                checked={form.is_required}
                onChange={(e) => update("is_required", e.target.checked)}
              />
              {t("admissions.documents_admin.field_required")}
            </label>
            <Field label={t("admissions.documents_admin.field_order")} compact>
              <input
                type="number"
                min="0"
                value={form.order_index}
                onChange={(e) => update("order_index", parseInt(e.target.value, 10))}
                className="input-soft w-20"
              />
            </Field>
          </div>
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onCancel}
              className="text-sm text-[#6B7280] px-4 py-2"
            >
              Cancel
            </button>
            <button
              type="submit"
              data-testid="admin-admissions-doc-save"
              className="btn-primary !py-2 !px-5 !text-sm inline-flex items-center gap-1.5"
            >
              <Save size={14} />
              {t("admissions.documents_admin.save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, compact }) {
  return (
    <label className={"block " + (compact ? "" : "")}>
      <span className="block text-xs uppercase tracking-[0.04em] text-[#6B7280] font-semibold mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
